import { useState, useEffect } from 'react';
import { getAllCountries } from '../../../services/firestoreService';
import { saveFund } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import ToggleField from '../../shared/Forms/ToggleField';
import MultiSelect from '../../shared/Forms/MultiSelect';
import PercentageField from '../../shared/Forms/PercentageField';
import Button from '../../shared/UI/Button';

const EMPTY_FORM = {
  name: '', short_name: '', vintage_year: '', fund_life_years: '',
  extension_provisions: '', base_currency: '', fund_size: '',
  status: 'active', has_streams: false,
  target_irr_gross: '', target_irr_net: '', hurdle_rate: '',
  target_moic: '', target_deployment_amount: '', target_num_investments: '',
  average_ticket_size: '',
  target_hold_period_years: '', max_single_obligor_pct: '',
  max_sector_concentration_pct: '', max_country_concentration_pct: '',
  equity_ratio_target: '', preferred_return: '', carry_percentage: '',
  deployment_territory: [],
  // Fix 2 Stage C — NEW funds default currency normalisation ON (creator can
  // still uncheck before saving). Existing funds are unaffected: the edit path
  // initialises from { ...fund }, not EMPTY_FORM, so their stored value stands.
  currency_normalisation_enabled: true,
};

export default function FundForm({ fund, onSaved, onCancel }) {
  const { user } = useAuth();
  const [form, setForm] = useState(fund ? { ...fund } : { ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    getAllCountries().then(list => setCountries(list.filter(c => c.is_active !== false)));
  }, []);

  const currencyOptions = countries.map(c => ({
    value: c.currency_code,
    label: `${c.currency_code} — ${c.currency_name}`,
  }));

  const countryOptions = countries.map(c => ({
    value: c.id,
    label: `${c.name} (${c.id})`,
  }));

  // Self-liquidating auto-calculates from equity ratio
  const selfLiquidatingRatio =
    form.equity_ratio_target !== '' && form.equity_ratio_target !== null
      ? parseFloat((1 - parseFloat(form.equity_ratio_target || 0)).toFixed(4))
      : '';

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function validate() {
    const e = {};
    if (!form.name?.trim()) e.name = 'Fund name is required';
    if (!form.short_name?.trim()) e.short_name = 'Short name is required';
    if (form.short_name?.length > 10) e.short_name = 'Short name must be 10 characters or fewer';
    if (!form.vintage_year) e.vintage_year = 'Vintage year is required';
    if (!form.fund_life_years) e.fund_life_years = 'Fund life is required';
    if (!form.base_currency) e.base_currency = 'Base currency is required';
    if (!form.fund_size) e.fund_size = 'Fund size is required';
    if (form.target_irr_gross === '') e.target_irr_gross = 'Required';
    if (form.target_irr_net === '') e.target_irr_net = 'Required';
    if (form.hurdle_rate === '') e.hurdle_rate = 'Required';
    if (!form.target_moic) e.target_moic = 'Required';
    if (!form.target_deployment_amount) e.target_deployment_amount = 'Required';
    if (!form.target_num_investments) e.target_num_investments = 'Required';
    if (!form.target_hold_period_years) e.target_hold_period_years = 'Required';
    if (form.max_single_obligor_pct === '') e.max_single_obligor_pct = 'Required';
    if (form.max_sector_concentration_pct === '') e.max_sector_concentration_pct = 'Required';
    if (form.max_country_concentration_pct === '') e.max_country_concentration_pct = 'Required';
    if (form.equity_ratio_target === '') e.equity_ratio_target = 'Required';
    if (form.preferred_return === '') e.preferred_return = 'Required';
    if (form.carry_percentage === '') e.carry_percentage = 'Required';
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const data = {
        ...form,
        vintage_year: parseInt(form.vintage_year, 10),
        fund_life_years: parseInt(form.fund_life_years, 10),
        fund_size: parseFloat(form.fund_size),
        target_moic: parseFloat(form.target_moic),
        target_deployment_amount: parseFloat(form.target_deployment_amount),
        target_num_investments: parseInt(form.target_num_investments, 10),
        // Optional. Blank → null (existing funds without it are unaffected).
        average_ticket_size:
          form.average_ticket_size === '' || form.average_ticket_size == null
            ? null
            : parseFloat(form.average_ticket_size),
        target_hold_period_years: parseFloat(form.target_hold_period_years),
        self_liquidating_ratio_target: selfLiquidatingRatio,
      };
      await saveFund({ fundData: data, performedBy: user.uid });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onSaved(); }, 1800);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }


  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {success && <div className="alert alert-success">Fund saved successfully!</div>}

      <div className="card mb-4">
        <div className="card-header"><h2>Basic Details</h2></div>
        <div className="card-body">
          <div className="form-grid">
            <FormField label="Fund Name" required error={errors.name}>
              <input className={`form-input ${errors.name ? 'error' : ''}`} value={form.name}
                onChange={e => set('name', e.target.value)} placeholder="e.g. MIA Fund I" />
            </FormField>
            <FormField label="Short Name" required error={errors.short_name} hint="Max 10 characters">
              <input className={`form-input ${errors.short_name ? 'error' : ''}`}
                value={form.short_name} maxLength={10}
                onChange={e => set('short_name', e.target.value)} placeholder="e.g. MIA1" />
            </FormField>
            <FormField label="Vintage Year" required error={errors.vintage_year}>
              <input type="number" className={`form-input ${errors.vintage_year ? 'error' : ''}`}
                value={form.vintage_year} onChange={e => set('vintage_year', e.target.value)}
                placeholder="e.g. 2019" min="2000" max="2100" />
            </FormField>
            <FormField label="Fund Life (years)" required error={errors.fund_life_years}>
              <input type="number" className={`form-input ${errors.fund_life_years ? 'error' : ''}`}
                value={form.fund_life_years} onChange={e => set('fund_life_years', e.target.value)}
                min="1" max="30" />
            </FormField>
            <SelectField label="Status" value={form.status} onChange={e => set('status', e.target.value)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'raising', label: 'Raising' },
                { value: 'closed', label: 'Closed' },
              ]} />
            <SelectField label="Base Currency" required error={errors.base_currency}
              value={form.base_currency} onChange={e => set('base_currency', e.target.value)}
              options={currencyOptions} placeholder="Select currency…" />
            <FormField label="Fund Size" required error={errors.fund_size}>
              <input type="number" className={`form-input ${errors.fund_size ? 'error' : ''}`}
                value={form.fund_size} onChange={e => set('fund_size', e.target.value)} min="0" />
            </FormField>
            <ToggleField label="Has Streams" checked={form.has_streams}
              onChange={v => set('has_streams', v)} />
            <ToggleField label="Currency Normalisation"
              checked={form.currency_normalisation_enabled}
              onChange={v => set('currency_normalisation_enabled', v)}
              hint="When enabled, financial figures submitted in different currencies will be automatically converted before scoring. Only enable after confirming your rubric score bands are defined in a single base currency." />
            <FormField label="Extension Provisions" className="form-full">
              <textarea className="form-textarea" value={form.extension_provisions || ''}
                onChange={e => set('extension_provisions', e.target.value)}
                placeholder="Describe any extension provisions…" rows={2} />
            </FormField>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header"><h2>Target Metrics</h2></div>
        <div className="card-body">
          <div className="form-grid">
            <PercentageField label="Target IRR (Gross)" required name="target_irr_gross"
              value={form.target_irr_gross} onChange={e => set('target_irr_gross', e.target.value)}
              error={errors.target_irr_gross} />
            <PercentageField label="Target IRR (Net)" required name="target_irr_net"
              value={form.target_irr_net} onChange={e => set('target_irr_net', e.target.value)}
              error={errors.target_irr_net} />
            <PercentageField label="Hurdle Rate" required name="hurdle_rate"
              value={form.hurdle_rate} onChange={e => set('hurdle_rate', e.target.value)}
              error={errors.hurdle_rate}
              hint="The minimum fund IRR the GP must achieve before earning carried interest. If the fund does not clear this rate, no carry is paid to the GP regardless of performance." />
            <FormField label="Target MOIC" required error={errors.target_moic}>
              <input type="number" className={`form-input ${errors.target_moic ? 'error' : ''}`}
                value={form.target_moic} onChange={e => set('target_moic', e.target.value)}
                min="0" step="0.1" placeholder="e.g. 2.5" />
            </FormField>
            <FormField label="Target Deployment Amount" required error={errors.target_deployment_amount}>
              <input type="number" className={`form-input ${errors.target_deployment_amount ? 'error' : ''}`}
                value={form.target_deployment_amount}
                onChange={e => set('target_deployment_amount', e.target.value)} min="0" />
            </FormField>
            <FormField label="Average Ticket Size"
              hint="Optional. Typical investment amount, in the fund's base currency. Used for the pipeline ticket-size band filter.">
              <input type="number" className="form-input"
                value={form.average_ticket_size ?? ''}
                onChange={e => set('average_ticket_size', e.target.value)}
                min="0" placeholder="e.g. 5000000" />
            </FormField>
            <FormField label="Target Number of Investments" required error={errors.target_num_investments}>
              <input type="number" className={`form-input ${errors.target_num_investments ? 'error' : ''}`}
                value={form.target_num_investments}
                onChange={e => set('target_num_investments', e.target.value)} min="1" />
            </FormField>
            <FormField label="Target Hold Period (years)" required error={errors.target_hold_period_years}>
              <input type="number" className={`form-input ${errors.target_hold_period_years ? 'error' : ''}`}
                value={form.target_hold_period_years}
                onChange={e => set('target_hold_period_years', e.target.value)} min="0" step="0.5" />
            </FormField>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header"><h2>Concentration Limits</h2></div>
        <div className="card-body">
          <div className="form-grid">
            <PercentageField label="Max Single Obligor %" required name="max_single_obligor_pct"
              value={form.max_single_obligor_pct}
              onChange={e => set('max_single_obligor_pct', e.target.value)}
              error={errors.max_single_obligor_pct} hint="e.g. enter 15 for 15%" />
            <PercentageField label="Max Sector Concentration %" required name="max_sector_concentration_pct"
              value={form.max_sector_concentration_pct}
              onChange={e => set('max_sector_concentration_pct', e.target.value)}
              error={errors.max_sector_concentration_pct} />
            <PercentageField label="Max Country Concentration %" required name="max_country_concentration_pct"
              value={form.max_country_concentration_pct}
              onChange={e => set('max_country_concentration_pct', e.target.value)}
              error={errors.max_country_concentration_pct} />
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header"><h2>Instrument Mix & Returns</h2></div>
        <div className="card-body">
          <div className="form-grid">
            <PercentageField label="Equity Ratio Target %" required name="equity_ratio_target"
              value={form.equity_ratio_target}
              onChange={e => set('equity_ratio_target', e.target.value)}
              error={errors.equity_ratio_target} />
            <FormField label="Self-Liquidating Ratio Target %"
              hint="Auto-calculated: 100% − Equity Ratio">
              <input type="text" className="form-input"
                value={selfLiquidatingRatio !== '' ? `${(selfLiquidatingRatio * 100).toFixed(2)}%` : '—'}
                readOnly style={{ background: 'var(--color-border-light)', cursor: 'not-allowed' }} />
            </FormField>
            <PercentageField label="Preferred Return %" required name="preferred_return"
              value={form.preferred_return} onChange={e => set('preferred_return', e.target.value)}
              error={errors.preferred_return}
              hint="The rate of return LPs receive on invested capital before any distributions are shared with the GP. Typically set equal to the hurdle rate. Governs the distribution waterfall order." />
            <PercentageField label="Carry Percentage %" required name="carry_percentage"
              value={form.carry_percentage} onChange={e => set('carry_percentage', e.target.value)}
              error={errors.carry_percentage} />
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header"><h2>Deployment Territory</h2></div>
        <div className="card-body">
          <MultiSelect
            label="Deployment Territory (Countries)"
            options={countryOptions}
            selected={form.deployment_territory || []}
            onChange={v => set('deployment_territory', v)}
          />
        </div>
      </div>

      <div className="flex-between">
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}>
          {fund ? 'Save Changes' : 'Create Fund'}
        </Button>
      </div>
    </form>
  );
}
